import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Box from "../components/Box";
import LoadingProgress from "../components/LoadingProgress";
import ErrorMessage from "../components/ErrorMessage";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  fetchPersistedSalesArqueos,
  fetchPersistedOperationalArqueos,
} from "../actions/arqueos";

// dashboard-specific styles (no reuse from other screens)
import "../styles/DashboardView.css";

function fmtCurrency(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// format a Date object as YYYY-MM-DD in local time (avoids UTC offset issues)
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return (Number(v) * 100).toFixed(1) + "%";
}

class OperationalDashboardView extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    // compute full current month interval
    const monthStart = new Date(year, month, 1);
    const monthEndDate = new Date(year, month + 1, 0); // last day of month
    const monthEnd = localIso(monthEndDate);

    // prepare last‑year equivalent interval
    const lyStartDate = new Date(year - 1, month, 1);
    const lyEndDate = new Date(year - 1, month + 1, 0);
    const lyStartStr = localIso(lyStartDate);
    const lyEndStr = localIso(lyEndDate);

    // comparison period defaults to last year same month

    this.state = {
      // origin / current period (committed)
      startDate: localIso(monthStart),
      endDate: monthEnd,
      // current period type (range/day/month/year)
      periodType: "range",
      // temporary inputs (editable, not applied until refresh)
      tempPeriodType: "range",
      tempStartDate: localIso(monthStart),
      tempEndDate: monthEnd,
      // comparison period (committed)
      compareStartDate: lyStartStr,
      compareEndDate: lyEndStr,
      // temporary comparison inputs
      tempCompareStartDate: lyStartStr,
      tempCompareEndDate: lyEndStr,
      loading: false,
      // filters matching the dashboard spec
      country: "",
      businessModel: "",
      activeOnly: true,
      siteFilter: "",
      // toggle for showing extra fetch filters
      showAdvancedFilters: false,
      // metric used for ranking top locations
      topMetric: 'gmv',
      // last-year/persisted results (kept in local state)
      lySalesArqueos: null,
      lyOperationalArqueos: null,
      // flags about comparison data:
      // - did the persisted endpoint return any rows?
      lyPersistedHasData: false,
      // - did any source (persisted or generated) return rows? used for UI hints
      lyHasData: false,
      // per-donut visibility toggles (slice keys hidden by user)
      installedHidden: {
        country: [],
        category: [],
        businessModel: [],
      },
      // SVG connector lines state (computed after render)
      donutConnectors: {},
    };

    // DOM refs used to compute connector positions
    this.containerRefs = { country: null, category: null, businessModel: null };
    this.donutRefs = { country: null, category: null, businessModel: null };
    this.legendItemRefs = { country: [], category: [], businessModel: [] };
    // chart data captured during render for connector calculation
    this.latestChartData = {};
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps, prevState) {
    // all header filters are now client-side only; data reload must be
    // initiated explicitly via the Refresh button. This lifecycle hook is
    // retained in case future non-filter state requires watching, but no
    // automatic fetch is performed.
  }

  componentWillUnmount() {
    // nothing to cleanup
  }

  // helper used by the "LY" button: look at the current temporary origin
  // period and populate the comparison inputs with the same interval one
  // year earlier.  Works for all four period types.
  fillLastYear = () => {
    const { tempPeriodType, tempStartDate, tempEndDate } = this.state;
    if (!tempStartDate) return;

    const shiftYearDate = (iso, delta) => {
      const d = new Date(iso);
      if (isNaN(d)) return "";
      d.setFullYear(d.getFullYear() + delta);
      return localIso(d);
    };

    let compStart = "";
    let compEnd = "";
    switch (tempPeriodType) {
      case "range":
        compStart = shiftYearDate(tempStartDate, -1);
        compEnd = tempEndDate ? shiftYearDate(tempEndDate, -1) : "";
        break;
      case "day":
        compStart = shiftYearDate(tempStartDate, -1);
        break;
      case "month": {
        // tempStartDate is yyyy-mm
        const [y, m] = tempStartDate.split("-");
        if (y && m) {
          compStart = `${String(Number(y) - 1).padStart(4, "0")}-${m}`;
        }
        break;
      }
      case "year": {
        const y = tempStartDate;
        if (y) {
          compStart = String(Number(y) - 1);
        }
        break;
      }
    }
    this.setState({
      tempCompareStartDate: compStart,
      tempCompareEndDate: compEnd,
    });
  };

  load = async () => {
    const {
      startDate,
      endDate,
      compareStartDate,
      compareEndDate,
      country,
      businessModel,
    } = this.state;
    // convert UI filter values into stored business model strings
    const makeBm = (bm) => {
      if (!bm) return undefined;
      const map = {
        revenue_share: 'REVENUE SHARE',
        alquiler: 'RENTAL',
        venta: 'SALE',
      };
      return map[bm] || bm;
    };
    const bmParam = makeBm(businessModel);

    this.setState({ loading: true });

    try {
      // 1) fetch comparison-period persisted data first (capture into local state)
      // guard against empty strings
      // default to current month when dates are empty
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const start = startDate ? new Date(startDate) : monthStart;
      const end = endDate ? new Date(endDate) : today;

      // if the user hasn't entered a compare range, skip loading LY data
      let lySalesAction = { response: [] };
      let lyOpAction = { response: [] };
      let persistedSalesCount = 0;
      if (compareStartDate && compareEndDate) {
        let lyStart = new Date(compareStartDate);
        let lyEnd = new Date(compareEndDate);
        const lyStartISO = localIso(lyStart);
        const lyEndISO = localIso(lyEnd);

        // try persisted first
        lySalesAction = await this.props.fetchPersistedSalesArqueos(
          lyStartISO,
          lyEndISO,
          bmParam,
          undefined,
          country || undefined,
        );
        if (lySalesAction && Array.isArray(lySalesAction.response)) {
          persistedSalesCount = lySalesAction.response.length;
        }

        lyOpAction = await this.props.fetchPersistedOperationalArqueos(
          lyStartISO,
          lyEndISO,
          undefined,
          undefined,
          country || undefined,
        );

        // fallback to generated endpoints when persisted LY docs are not available
        if (persistedSalesCount === 0) {
          try {
            lySalesAction = await this.props.fetchGeneratedSalesArqueos(
              lyStartISO,
              lyEndISO,
              businessModel || undefined,
              undefined,
              country || undefined,
            );
          } catch (e) {
            lySalesAction = { response: [] };
          }
        }

        if (
          !lyOpAction ||
          !Array.isArray(lyOpAction.response) ||
          lyOpAction.response.length === 0
        ) {
          try {
            lyOpAction = await this.props.fetchGeneratedOperationalArqueos(
              lyStartISO,
              lyEndISO,
              undefined,
              undefined,
              country || undefined,
            );
          } catch (e) {
            lyOpAction = { response: [] };
          }
        }
      }

      this.setState({
        lySalesArqueos: (lySalesAction && lySalesAction.response) || [],
        lyOperationalArqueos: (lyOpAction && lyOpAction.response) || [],
        // persisted data flag (sales only)
        lyPersistedHasData: persistedSalesCount > 0,
        // mark if either sales or operational arrays contained any rows
        lyHasData:
          (lySalesAction &&
            Array.isArray(lySalesAction.response) &&
            lySalesAction.response.length > 0) ||
          (lyOpAction &&
            Array.isArray(lyOpAction.response) &&
            lyOpAction.response.length > 0),
      });

      // 2) fetch current-period persisted data (populate Redux store used elsewhere)
      await Promise.all([
        this.props.fetchPersistedSalesArqueos(
          startDate,
          endDate,
          bmParam,
          undefined,
          country || undefined,
        ),
        this.props.fetchPersistedOperationalArqueos(
          startDate,
          endDate,
          undefined,
          undefined,
          country || undefined,
          bmParam,
        ),
      ]);

      this.setState({ loading: false });
    } catch (err) {
      this.setState({ loading: false });
    }
  };

  // Aggregations
  aggregateSales() {
    // apply business model & country filters client-side as well
    let arr = Array.isArray(this.props.generatedSalesArqueos)
      ? this.props.generatedSalesArqueos.slice()
      : [];
    // filter by selected period
    const { startDate, endDate, businessModel, country } = this.state;
    if (startDate || endDate) {
      arr = arr.filter((r) => {
        const day = r.Day
          ? r.Day.slice
            ? r.Day.slice(0, 10)
            : String(r.Day).slice(0, 10)
          : null;
        if (!day) return false;
        if (startDate && day < startDate) return false;
        if (endDate && day > endDate) return false;
        return true;
      });
    }
    if (businessModel) {
      const map = {
        revenue_share: 'REVENUE SHARE',
        alquiler: 'RENTAL',
        venta: 'SALE',
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.CustomerCountry || r.Country || r.SiteCountry || "")
            .toString()
            .toLowerCase()
            .includes(q),
      );
    }
    return this.aggregateFromRows(arr);
  }

  aggregateFromRows(rows) {
    const { siteFilter, activeOnly } = this.state;
    let arr = Array.isArray(rows) ? rows.slice() : [];

    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim().toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.CustomerName || "").toString().toLowerCase().includes(q),
      );
    }

    if (activeOnly) {
      arr = arr.filter(
        (r) =>
          (Number(r.GMV_gross || r.SalesTotal || 0) || 0) > 0 ||
          (Number(r.TotalTickets || 0) || 0) > 0,
      );
    }

    const gmv = arr.reduce(
      (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0),
      0,
    );
    const commission = arr.reduce(
      (s, r) => s + (Number(r.CommissionAmount || 0) || 0),
      0,
    );
    const transactions = arr.reduce(
      (s, r) => s + (Number(r.TotalTickets || 0) || 0),
      0,
    );
    const ticketAvg = transactions > 0 ? gmv / transactions : null;
    return { gmv, commission, transactions, ticketAvg, rows: arr };
  }

  aggregateOperational(opts = {}) {
    const { siteFilter, activeOnly, businessModel: bmState, country } = this.state;
    const ignoreActive = opts.ignoreActive || false;
    // allow callers to override which business model filter is used
    const businessModel = opts.businessModel !== undefined ? opts.businessModel : bmState;
    let arr = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos
      : [];

    // apply business-model filter client-side as well
    if (businessModel) {
      const map = {
        revenue_share: 'REVENUE SHARE',
        alquiler: 'RENTAL',
        venta: 'SALE',
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }
    // apply country filter
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.Country || r.CustomerCountry || r.SiteCountry || "")
            .toString()
            .toLowerCase()
            .includes(q),
      );
    }

    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim().toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.CustomerName || "").toString().toLowerCase().includes(q),
      );
    }

    // Spec: Activa si GMV > 0 en el periodo, o Transacciones > 0 en el periodo, o reporting
    if (activeOnly && !ignoreActive) {
      arr = arr.filter(
        (r) =>
          (Number(r.GMV_gross || r.SalesTotal || 0) > 0) ||
          (Number(r.TotalTickets || 0) > 0) ||
          !!r.isReporting
      );
    }

    // occupancy adjusted per doc = LockersOccupied / (TotlockerStatus.total - TotlockerStatus.fault)
    const occs = arr.map((r) => {
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const denom = total - fault;
      const occ =
        denom > 0 ? (Number(r.LockersOccupied || 0) || 0) / denom : null;
      const avail = total > 0 ? 1 - fault / total : null;
      return {
        occ,
        avail,
        total,
        fault,
        site: r.Location,
        machine: r.MachineSerial,
        isReporting: !!r.isReporting,
        incidencesOpened: Number(r.IncidencesOpened || 0),
        Day: r.Day,
      };
    });

    const avgOcc = occs
      .filter((o) => o.occ !== null)
      .reduce((s, o) => s + o.occ, 0);
    const occCount = occs.filter((o) => o.occ !== null).length;
    const avgOccFinal = occCount > 0 ? avgOcc / occCount : null;

    const avgAvail = occs
      .filter((o) => o.avail !== null)
      .reduce((s, o) => s + o.avail, 0);
    const availCount = occs.filter((o) => o.avail !== null).length;
    const avgAvailFinal = availCount > 0 ? avgAvail / availCount : null;

    const totalFaulty = occs.reduce((s, o) => s + (o.fault || 0), 0);
    const totalLockers = occs.reduce((s, o) => s + (o.total || 0), 0);

    const machinesReporting = occs.filter((o) => o.isReporting).length;
    const machinesTotal = occs.length;

    const incidences = occs.reduce((s, o) => s + (o.incidencesOpened || 0), 0);

    return {
      avgOccFinal,
      avgAvailFinal,
      totalFaulty,
      totalLockers,
      machinesReporting,
      machinesTotal,
      incidences,
      occs,
      raw: arr,
    };
  }

  // Average lockers available per day across the selected period (avg_dias)
  computeAvgLockersAvailable() {
    const opRows = this.aggregateOperational().raw || [];
    if (!opRows.length) return null;

    // group by day
    const byDay = {};
    opRows.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const available = Math.max(0, total - fault);
      byDay[day] = (byDay[day] || 0) + available;
    });

    const days = Object.keys(byDay);
    if (!days.length) return null;
    const sum = days.reduce((s, d) => s + byDay[d], 0);
    return sum / days.length;
  }

  // same as computeAvgLockersAvailable but over arbitrary rows
  computeAvgLockersAvailableFromRows(rows) {
    const arr = Array.isArray(rows) ? rows : [];
    if (!arr.length) return null;

    const byDay = {};
    arr.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const available = Math.max(0, total - fault);
      byDay[day] = (byDay[day] || 0) + available;
    });

    const days = Object.keys(byDay);
    if (!days.length) return null;
    const sum = days.reduce((s, d) => s + byDay[d], 0);
    return sum / days.length;
  }

  // Aggregate operational metrics from arbitrary rows (used for LY comparisons)
  aggregateOperationalFromRows(rows) {
    const { businessModel } = this.state;
    let arr = Array.isArray(rows) ? rows.slice() : [];
    if (businessModel) {
      const map = {
        revenue_share: 'REVENUE SHARE',
        alquiler: 'RENTAL',
        venta: 'SALE',
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }

    const occs = arr.map((r) => {
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const denom = total - fault;
      const occ =
        denom > 0 ? (Number(r.LockersOccupied || 0) || 0) / denom : null;
      const avail = total > 0 ? 1 - fault / total : null;
      return {
        occ,
        avail,
        total,
        fault,
        site: r.Location,
        machine: r.MachineSerial,
        isReporting: !!r.isReporting,
        incidencesOpened: Number(r.IncidencesOpened || 0),
        Day: r.Day,
      };
    });

    const avgOcc = occs
      .filter((o) => o.occ !== null)
      .reduce((s, o) => s + o.occ, 0);
    const occCount = occs.filter((o) => o.occ !== null).length;
    const avgOccFinal = occCount > 0 ? avgOcc / occCount : null;

    const avgAvail = occs
      .filter((o) => o.avail !== null)
      .reduce((s, o) => s + o.avail, 0);
    const availCount = occs.filter((o) => o.avail !== null).length;
    const avgAvailFinal = availCount > 0 ? avgAvail / availCount : null;

    const totalFaulty = occs.reduce((s, o) => s + (o.fault || 0), 0);
    const totalLockers = occs.reduce((s, o) => s + (o.total || 0), 0);

    const machinesReporting = occs.filter((o) => o.isReporting).length;
    const machinesTotal = occs.length;

    const incidences = occs.reduce((s, o) => s + (o.incidencesOpened || 0), 0);

    return {
      avgOccFinal,
      avgAvailFinal,
      totalFaulty,
      totalLockers,
      machinesReporting,
      machinesTotal,
      incidences,
      occs,
      raw: arr,
    };
  }

  groupBySiteTop5(salesRows) {
    const map = {};
    salesRows.forEach((r) => {
      const site = r.Site || r.Location || r.SiteName || "Unknown";
      if (!map[site])
        map[site] = {
          site,
          gmv: 0,
          commission: 0,
          transactions: 0,
          occupancySum: 0,
          occupancyCount: 0,
          incidents: 0,
        };
      map[site].gmv += Number(r.GMV_gross || r.SalesTotal || 0) || 0;
      map[site].commission += Number(r.CommissionAmount || 0) || 0;
      map[site].transactions += Number(r.TotalTickets || 0) || 0;
      map[site].incidents += Number(r.IncidencesOpened || 0) || 0;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.gmv - a.gmv);
    return arr.slice(0, 5);
  }

  // base installed donuts computed from operational docs (latest snapshot within the selected range)
  // helper used to normalize the various fields we treat as the site/customer
  // identifier. this ensures every filter and datalist uses the same set of
  // properties (Fullname, SiteName, Site, Location, CustomerName, MachineSerial).
  // NOTE: old `siteString` helper removed – filtering now only uses
  // the explicit `CustomerName` field per user request.

  computeInstalledBy(arr, key, asOf) {
    // We want the latest snapshot for each machine, not every day of the range.
    // Take the row with the maximum `Day` value per machine and aggregate that.
    const latest = {};
    // allow calling code to specify the date at which the snapshot should be
    // evaluated (compare period end), otherwise fall back to now.
    const today = asOf ? new Date(asOf) : new Date();

    arr.forEach((r) => {
      // Only include machines that are installed (installed_at <= today) and not removed (removed_at null or > today)
      if (
        (r.installed_at && new Date(r.installed_at) > today) ||
        (r.removed_at && new Date(r.removed_at) <= today)
      ) {
        return;
      }
      const machineId = r.MachineSerial || r.MachineId || r.Serial || r._id || "unknown";
      const day = r.Day ? new Date(r.Day) : null;
      if (!latest[machineId] || (day && latest[machineId].Day && day > new Date(latest[machineId].Day))) {
        latest[machineId] = r;
      }
    });

    const map = {};
    Object.values(latest).forEach((r) => {
      const group = r[key] || "Unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      map[group] = (map[group] || 0) + (total || 0);
    });

    const items = Object.keys(map).map((k) => ({ key: k, value: map[k] }));
    const total = items.reduce((s, i) => s + i.value, 0);
    // `overallTotal` was never defined here; we only compute the raw total and
    // percentages. Any display override is handled by the caller.
    items.forEach((i) => (i.pct = total > 0 ? i.value / total : 0));
    items.sort((a, b) => b.value - a.value);
    return { items, total };
  }

  // Render a single installed-donut card (KPI-style header + two-column body)
  // extended with optional compareData: {items,total}
  renderInstalledDonutCard(title, data, groupKey, iconName = "layer-group", overallTotal = null, compareData = null) {
    const colors = [
    '#0b97ff', // bright blue
    '#06b6d4', // cyan
    '#10b981', // teal/green
    '#f59e0b', // amber
    '#ec4899', // pink
    '#8b5cf6', // violet
    '#f97316', // orange
    '#ef4444', // red
    '#6366f1'  // indigo
    ];
    const makeItems = (src) => {
      const base = (src.items || []).slice(0, 6);
      const oth = Math.max(
        0,
        (src.total || 0) - base.reduce((s, i) => s + i.value, 0),
      );
      return oth > 0
        ? [
            ...base,
            { key: "Other", value: oth, pct: oth / (src.total || 1) },
          ]
        : base;
    };
    const items = makeItems(data);
    const chartData = items.map((i) => ({
      name: i.key,
      value: i.value,
      pct: i.pct,
    }));
    const total = data.total || chartData.reduce((s, i) => s + i.value, 0);
    const displayTotal = overallTotal !== null ? overallTotal : total;

    // prepare comparison chart info if provided and non-empty
    let compareChartData = null;
    let compareTotal = 0;
    if (compareData && (compareData.total || 0) > 0) {
      const compItems = makeItems(compareData);
      compareTotal = compareData.total || compItems.reduce((s, i) => s + i.value, 0);
      // ignore if there are no meaningful slices
      if (compItems.length > 0) {
        compareChartData = compItems.map((i) => ({
          name: i.key,
          value: i.value,
          pct: i.pct,
        }));
      }
    }

    // capture chartData for connector calculation after paint
    this.latestChartData = this.latestChartData || {};
    this.latestChartData[groupKey] = chartData;
    this.legendItemRefs[groupKey] = [];

    // show everything (no toggling / hide/visible labels)
    const visibleChartData = chartData;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* header: icon + title (KPI-like) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i
            className={`fas fa-${iconName} mr-2`}
            style={{ color: "#6b7280" }}
          ></i>
          <div style={{ fontWeight: 700 }}>{title}</div>
        </div>

        {/* body: two columns — donut (left) + list (right) */}
        <div
          ref={(el) => (this.containerRefs[groupKey] = el)}
          className="donut-container"
          style={{
            position: "relative",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            marginTop: 30,
            // wrapping controlled by CSS media query
          }}
        >
          <div
            ref={(el) => (this.donutRefs[groupKey] = el)}
            style={{
              width: 320,
              height: 220,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PieChart width={320} height={220}>
              {/* outer ring: current period */}
              <Pie
                data={visibleChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={90}
                paddingAngle={2}
                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                labelLine={{
                  stroke: "#6b7280",
                  strokeDasharray: "3 4",
                  strokeWidth: 1,
                }}
              >
                {visibleChartData.map((entry, idx) => (
                  <Cell
                    key={`cell-${groupKey}-${idx}`}
                    fill={colors[idx % colors.length]}
                  />
                ))}
              </Pie>
              {/* inner ring: comparison period */}
              {compareChartData && (
                <Pie
                  data={compareChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={55}
                  paddingAngle={1}
                  label={false}  // no labels on inner donut
                  labelLine={false}
                >
                  {compareChartData.map((entry, idx) => (
                    <Cell
                      key={`cell2-${groupKey}-${idx}`}
                      fill={colors[idx % colors.length]}
                      fillOpacity={0.5}
                    />
                  ))}
                </Pie>
              )}
              <Tooltip
                formatter={(value, name) => [
                  `${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                  name,
                ]}
                wrapperStyle={{ zIndex: 10 }}
              />
            </PieChart>
            {/* central total overlay */}
            <div
              style={{
                position: "absolute",
                textAlign: "center",
                pointerEvents: "none",
                zIndex: 1, // keep below tooltip
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {displayTotal}
              </div>
              {compareChartData && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  vs {compareTotal}
                </div>
              )}
              <div style={{ fontSize: 12, color: "#6b7280" }}>Total</div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((it, idx) => {
                const compItem =
                  compareChartData && compareChartData.length > 0
                    ? compareChartData.find((c) => c.name === it.key)
                    : null;
                return (
                  <div
                    key={it.key}
                    ref={(el) => (this.legendItemRefs[groupKey][idx] = el)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        background: colors[idx % colors.length],
                        borderRadius: 3,
                        marginTop: 3,
                      }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", marginLeft: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{it.key}</div>
                      <div className="text-muted small" style={{ marginTop: 2 }}>
                        {it.value} {`(${(it.pct * 100).toFixed(1)}%)`}
                        {compItem && compItem.value != null && (
                          <>
                            {' '}vs {compItem.value} {`(${(compItem.pct * 100).toFixed(1)}%)`}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderKPIs() {
    const { gmv, commission, transactions, ticketAvg } = this.aggregateSales();
    const op = this.aggregateOperational();

    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">GMV</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {fmtCurrency(gmv)} €
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Comisión neta</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {fmtCurrency(commission)} €
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Take rate</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {commission && gmv ? pct(commission / gmv) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Transacciones</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{transactions}</div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Ticket medio</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {ticketAvg ? fmtCurrency(ticketAvg) + " €" : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 220 }}>
          <div className="text-muted">Ocupación ajustada (promedio)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {op.avgOccFinal !== null ? pct(op.avgOccFinal) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Disponibilidad (promedio)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {op.avgAvailFinal !== null ? pct(op.avgAvailFinal) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Incidencias</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{op.incidences}</div>
        </div>
        <div className="card p-3" style={{ minWidth: 220 }}>
          <div className="text-muted">% máquinas reportando (end date)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {op.machinesTotal > 0
              ? `${Math.round((op.machinesReporting / op.machinesTotal) * 100)}% (${op.machinesReporting}/${op.machinesTotal})`
              : "—"}
          </div>
        </div>
      </div>
    );
  }

  // Combined GMV + Commission trend used by the 'trend' panel
  // compute daily per-locker and totals for evolution charts
  computeDailyMetrics() {
    const sales = this.aggregateSales().rows || [];
    const opRows = this.aggregateOperational().raw || [];
    const byDaySales = {};
    sales.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      if (!byDaySales[day]) byDaySales[day] = { gmv: 0, commission: 0 };
      // use net GMV for metrics, falling back to SalesTotal for legacy rows
      byDaySales[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      byDaySales[day].commission += Number(r.CommissionAmount || 0) || 0;
    });
    const byDayAvail = {};
    opRows.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const avail = Math.max(0, total - fault);
      byDayAvail[day] = (byDayAvail[day] || 0) + avail;
    });
    const days = Array.from(
      new Set([...Object.keys(byDaySales), ...Object.keys(byDayAvail)]),
    ).sort();
    return days.map((d) => {
      const totals = byDaySales[d] || { gmv: 0, commission: 0 };
      const avail = byDayAvail[d] || 0;
      return {
        day: d,
        gmvPer: avail > 0 ? totals.gmv / avail : null,
        comPer: avail > 0 ? totals.commission / avail : null,
        gmv: totals.gmv,
        commission: totals.commission,
      };
    });
  }

  renderLockerCard(
    label,
    value,
    chartData,
    type = "bar",
    icon = "chart-bar",
    deltaPct = null,
    options = {},
  ) {
    // optionally hide entire card if there is nothing to show
    if (options.hideIfEmpty) {
      const emptyData = !chartData || chartData.length === 0;
      if (emptyData) {
        return null;
      }
    }

    const formatted =
      value !== null && value !== undefined ? `${fmtCurrency(value)} €` : "—";

    // determine if we should display a delta row at all
    const { compareStartDate, compareEndDate, lyPersistedHasData } = this.state;
    const hasCompareRange = compareStartDate && compareEndDate;

    let deltaElement = null;
    if (hasCompareRange) {
      if (deltaPct !== null && deltaPct !== undefined && lyPersistedHasData) {
        const pos = deltaPct > 0;
        const color = pos
          ? "text-success"
          : deltaPct < 0
            ? "text-danger"
            : "text-muted";
        const sign = pos ? "+" : deltaPct < 0 ? "-" : "";
        deltaElement = (
          <div
            className="small text-muted"
            style={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>vs&nbsp;LY:</span>
            <span className={color}>
              {sign}
              {Math.abs((deltaPct * 100).toFixed(1))}%
            </span>
          </div>
        );
      } else {
        // comparison requested but either no persisted data or delta not computable
        deltaElement = (
          <div className="small text-muted" style={{ marginTop: 2 }}>
            vs LY: n/a
          </div>
        );
      }
    }

    const showValueRow = value !== null && value !== undefined;

    return (
      <div
        className="dashboard-card-section"
        style={{ minHeight: (type === "line" ? 340 : 140), flexDirection: "column", padding: "12px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className={`fas fa-${icon} mr-2`} style={{ color: "#6b7280" }}></i>
          <div style={{ fontWeight: 700 }}>{label}</div>
        </div>

        {showValueRow && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                margin: "6px 0",
              }}
            >
              <div
                className="kpi-value"
                style={{ fontSize: 24, fontWeight: 700 }}
              >
                {formatted}
              </div>
              {deltaElement}
            </div>
          )}

        <div style={{ width: "100%", height: type === "bar" ? 80 : type === "line" ? 300 : 60 }}>
          {type === "bar" ? (
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                <Tooltip formatter={(v) => `${fmtCurrency(v)} €`} />
                <Bar dataKey="value" fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer>
          ) : // if no trend data, show placeholder rather than empty flat lines
          !chartData || chartData.length === 0 ? (
            <div
              style={{
                width: "100%",
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6b7280",
                fontSize: 12,
              }}
            >
              sin variación
            </div>
          ) : (
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                <CartesianGrid strokeDasharray="3 3" />
                <Legend layout="horizontal" verticalAlign="top" height={20} />
                <Tooltip formatter={(v) => `${fmtCurrency(v)} €`} />
                <Line
                  type="monotone"
                  dataKey="gmv"
                  stroke="#2563EB"
                  dot={{ r: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="commission"
                  stroke="#059669"
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  renderLockerKPIs() {
    const daily = this.computeDailyMetrics();
    const { gmv, commission } = this.aggregateSales();
    const opAgg = this.aggregateOperational();
    const totalLockers = opAgg.totalLockers || 0;

    // for commission per locker we only count revenue-share lockers
    const rsAgg = this.aggregateOperational({ businessModel: 'REVENUE SHARE' });
    const totalRsLockers = rsAgg.totalLockers || 0;

    const perLockerGMV =
      totalLockers > 0 ? gmv / totalLockers : null;
    const perLockerCom =
      totalRsLockers > 0 ? commission / totalRsLockers : null;

    // compute last-year equivalents if present
    let lyPerLockerGMV = null;
    let lyPerLockerCom = null;
    if (
      Array.isArray(this.state.lySalesArqueos) &&
      Array.isArray(this.state.lyOperationalArqueos)
    ) {
      // only compute last-year per-locker values if persisted sales data
      // exists; otherwise we treat it as unavailable even if generated
      // fallback returned something.
      if (this.state.lyPersistedHasData) {
        // compute LY totals including revenue-share locker count
        const lyOpAgg = this.aggregateOperationalFromRows(
          this.state.lyOperationalArqueos,
        );
        const lyTotalLockers = lyOpAgg.totalLockers || 0;
        const lyRsOpAgg = this.aggregateOperationalFromRows(
          this.state.lyOperationalArqueos.filter(r => r.BusinessModel === 'REVENUE SHARE'),
        );
        const lyRsLockers = lyRsOpAgg.totalLockers || 0;
        const lyGmvTotal = this.state.lySalesArqueos.reduce(
          (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0),
          0,
        );
        const lyComTotal = this.state.lySalesArqueos.reduce(
          (s, r) => s + (Number(r.CommissionAmount || 0) || 0),
          0,
        );
        if (lyTotalLockers > 0) {
          lyPerLockerGMV = lyGmvTotal / lyTotalLockers;
        }
        if (lyRsLockers > 0) {
          lyPerLockerCom = lyComTotal / lyRsLockers;
        }
      }
    }

    const deltaGmvPct =
      lyPerLockerGMV !== null && lyPerLockerGMV !== 0
        ? (perLockerGMV - lyPerLockerGMV) / (lyPerLockerGMV || 1)
        : null;
    const deltaComPct =
      lyPerLockerCom !== null && lyPerLockerCom !== 0
        ? (perLockerCom - lyPerLockerCom) / (lyPerLockerCom || 1)
        : null;

    const chart1 = daily.map((d) => ({ name: d.day, value: d.gmvPer || 0 }));
    const chart2 = daily.map((d) => ({ name: d.day, value: d.comPer || 0 }));
    // For the trend we need total GMV/commission per day (not per‑locker)
    const chart3 = daily.map((d) => ({
      name: d.day,
      gmv: d.gmv || 0,
      commission: d.commission || 0,
    }));

    // determine whether the trend data contains any real variation; if not,
    // we will suppress the chart and just show n/a
    const hasTrendVariation = (() => {
      if (chart3.length < 2) return false;
      for (let i = 1; i < chart3.length; i++) {
        if (
          chart3[i].gmv !== chart3[i - 1].gmv ||
          chart3[i].commission !== chart3[i - 1].commission
        ) {
          return true;
        }
      }
      return false;
    })();

    // build accumulated series (gmv and commission) for daily totals
    const accumData = [];
    let cumGmv = 0,
      cumCom = 0;
    chart3.forEach((d) => {
      cumGmv += d.gmv || 0;
      cumCom += d.commission || 0;
      accumData.push({ name: d.name, gmv: cumGmv, commission: cumCom });
    });

    const hasAccumVariation = (() => {
      if (accumData.length < 2) return false;
      for (let i = 1; i < accumData.length; i++) {
        if (
          accumData[i].gmv !== accumData[i - 1].gmv ||
          accumData[i].commission !== accumData[i - 1].commission
        ) {
          return true;
        }
      }
      return false;
    })();

    return (
      <>
        <div className="dashboard-kpi-row mb-3">
          {this.renderLockerCard(
            "GMV por locker disponible",
            perLockerGMV,
            chart1,
            "bar",
            "boxes",
            deltaGmvPct,
          )}
          {this.renderLockerCard(
            "Comisión neta por locker disponible",
            perLockerCom,
            chart2,
            "bar",
            "hand-holding-usd",
            deltaComPct,
          )}
        </div>
        <div className="dashboard-kpi-row mb-3">
          {this.renderLockerCard(
            "Tendencia acumulada GMV vs comisión",
            null,
            hasAccumVariation ? accumData : [],
            "line",
            "chart-area",
            null,
            { hideIfEmpty: true },
          )}
        </div>
      </>
    );
  }

  renderGMVAndCommissionTrend() {
    const sales = this.aggregateSales().rows || [];

    // compute daily totals map
    const byDay = {};
    sales.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      if (!byDay[day]) byDay[day] = { gmv: 0, commission: 0 };
      byDay[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      byDay[day].commission += Number(r.CommissionAmount || 0) || 0;
    });

    const days = Object.keys(byDay).sort();
    if (!days.length)
      return (
        <div className="text-muted">
          No sales data for the selected period / filters
        </div>
      );

    const maxGMV = Math.max(...days.map((d) => byDay[d].gmv)) || 1;
    return (
      <div>
        <div style={{ display: "flex", gap: 8 }}>
          {days.map((d) => (
            <div key={d} style={{ flex: 1, textAlign: "center" }}>
              <div
                style={{
                  height: 100,
                  background: "#f5f7fb",
                  borderRadius: 4,
                  padding: 8,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "70%",
                    height: `${(byDay[d].gmv / maxGMV) * 100}%`,
                    background: "#2f6fdb",
                    borderRadius: 4,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 4,
                      bottom: 2,
                      width: "60%",
                      height: `${Math.min(100, (byDay[d].commission / (byDay[d].gmv || 1)) * 100)}%`,
                      background: "#28a745",
                      borderRadius: 3,
                      opacity: 0.95,
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: 6, fontSize: 11 }}>{d}</div>
              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600 }}>
                GMV {fmtCurrency(byDay[d].gmv)} € • Commission {fmtCurrency(byDay[d].commission)} €
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderInstalledDonuts() {
    const opRows = this.aggregateOperational().raw || [];
    const asOfCurrent = this.state.endDate ? new Date(this.state.endDate) : undefined;
    const byCountry = this.computeInstalledBy(opRows, "Country", asOfCurrent);
    const byCategory = this.computeInstalledBy(opRows, "Category", asOfCurrent); // preparado para 'vertical'
    const byBusiness = this.computeInstalledBy(opRows, "BusinessModel", asOfCurrent);

    const { installedHidden } = this.state || {
      installedHidden: { country: [], category: [], businessModel: [] },
    };
    const isHidden = (group, key) =>
      installedHidden &&
      installedHidden[group] &&
      installedHidden[group].includes(key);
    const toggleInstalledSlice = (group, key) => {
      this.setState((prev) => {
        const prevArr =
          (prev.installedHidden && prev.installedHidden[group]) || [];
        const exists = prevArr.includes(key);
        const nextArr = exists
          ? prevArr.filter((k) => k !== key)
          : prevArr.concat([key]);
        return {
          installedHidden: {
            ...(prev.installedHidden || {}),
            [group]: nextArr,
          },
        };
      });
    };

    const colors = [
      "#2f6fdb",
      "#28a745",
      "#ffb020",
      "#ef4444",
      "#6f42c1",
      "#0ea5a0",
      "#7c3aed",
      "#f97316",
    ];

    const renderDonutCard = (title, data, groupKey) => {
      const itemsBase = (data.items || []).slice(0, 6);
      const other = Math.max(
        0,
        (data.total || 0) - itemsBase.reduce((s, i) => s + i.value, 0),
      );
      const items =
        other > 0
          ? [
              ...itemsBase,
              { key: "Other", value: other, pct: other / (data.total || 1) },
            ]
          : itemsBase;

      if (!items.length)
        return (
          <div className="dashboard-card-section" style={{ minWidth: 280 }}>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <div className="text-muted small mt-2">No installation data</div>
          </div>
        );

      const chartData = items.map((i) => ({
        name: i.key,
        value: i.value,
        pct: i.pct,
      }));
      const visibleChartData = chartData.filter(
        (d) => !isHidden(groupKey, d.name),
      );
      const total = data.total || chartData.reduce((s, i) => s + i.value, 0);

      return (
        <div
          className="dashboard-card-section"
          style={{ minWidth: 320, flex: "1 1 320px" }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 180, height: 160 }}>
              <PieChart width={180} height={160}>
                {/* smaller hole + thicker stroke for better visibility */}
                <Pie
                  data={visibleChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  paddingAngle={2}
                  label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={{
                    stroke: "#6b7280",
                    strokeDasharray: "3 4",
                    strokeWidth: 1,
                  }}
                  onClick={(entry) =>
                    toggleInstalledSlice(groupKey, entry.name)
                  }
                >
                  {visibleChartData.map((entry, idx) => (
                    <Cell
                      key={`cell-${groupKey}-${idx}`}
                      fill={colors[idx % colors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{title}</div>
              <div className="text-muted small">Total lockers: {total}</div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {items.map((it, idx) => {
                  const hidden = isHidden(groupKey, it.key);
                  const displayPct =
                    it.pct !== undefined
                      ? (it.pct * 100).toFixed(1)
                      : total > 0
                        ? ((it.value / total) * 100).toFixed(1)
                        : "0.0";
                  return (
                    <div
                      key={it.key}
                      onClick={() => toggleInstalledSlice(groupKey, it.key)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        opacity: hidden ? 0.42 : 1,
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          background: colors[idx % colors.length],
                          borderRadius: 3,
                        }}
                      />
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {it.key}
                      </div>
                      <div
                        className="text-muted small"
                        style={{ marginLeft: 6 }}
                      >
                        {it.value} • {displayPct}%
                      </div>
                      <div
                        className="text-muted small"
                        style={{ marginLeft: "auto" }}
                      >
                        {hidden ? "hidden" : "visible"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div
        style={{
          display: "flex",
          gap: 32,        // extra space so percent labels have room
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {renderDonutCard("Instaladas por país", byCountry, "country")}
        {renderDonutCard("Instaladas por vertical", byCategory, "category")}
        {renderDonutCard(
          "Instaladas por modelo negocio",
          byBusiness,
          "businessModel",
        )}
      </div>
    );
  }

  renderTopLocations() {
    // gather data from sales and operational rows per site
    let salesRows = this.aggregateSales().rows || [];
    let opRows = this.aggregateOperational().raw || [];
    const { activeOnly, siteFilter } = this.state;

    // apply customer-name filter if present
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim().toLowerCase();
      const match = (r) =>
        (r.CustomerName || "").toString().toLowerCase().includes(q);
      salesRows = salesRows.filter(match);
      opRows = opRows.filter(match);
    }

    const map = {};
    salesRows.forEach((r) => {
      const site = r.Site || r.Location || r.SiteName || "Unknown";
      if (!map[site]) {
        map[site] = {
          site,
          customer: r.CustomerName || r.Customer || "",
          country: r.Country || r.SiteCountry || "",
          gmv: 0,
          commission: 0,
          transactions: 0,
          incidents: 0,
          occSum: 0,
          occCount: 0,
          availSum: 0,
          availCount: 0,
        };
      }
      map[site].gmv += Number(r.GMV_gross || r.SalesTotal || 0) || 0;
      map[site].commission += Number(r.CommissionAmount || 0) || 0;
      map[site].transactions += Number(r.TotalTickets || 0) || 0;
      map[site].incidents += Number(r.IncidencesOpened || 0) || 0;
    });

    opRows.forEach((r) => {
      const site = r.Location || r.Site || r.SiteName || "Unknown";
      if (!map[site]) {
        map[site] = {
          site,
          customer: "",
          country: "",
          gmv: 0,
          commission: 0,
          transactions: 0,
          incidents: 0,
          occSum: 0,
          occCount: 0,
          availSum: 0,
          availCount: 0,
        };
      }
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const denom = total - fault;
      const occ = denom > 0 ? (Number(r.LockersOccupied || 0) || 0) / denom : null;
      const avail = total > 0 ? 1 - fault / total : null;
      if (occ !== null) {
        map[site].occSum += occ;
        map[site].occCount += 1;
      }
      if (avail !== null) {
        map[site].availSum += avail;
        map[site].availCount += 1;
      }
      map[site].incidents += Number(r.IncidencesOpened || 0) || 0;
    });

    // build array and compute averages
    let arr = Object.values(map).map((s) => {
      const avgOcc = s.occCount ? s.occSum / s.occCount : null;
      const avgAvail = s.availCount ? s.availSum / s.availCount : null;
      return { ...s, avgOcc, avgAvail };
    });

    if (activeOnly) {
      arr = arr.filter((s) => s.gmv > 0 || s.transactions > 0 || s.incidents > 0);
    }

    const metrics = [
      { key: "gmv", label: "GMV" },
      { key: "transactions", label: "Transacciones" },
      { key: "commission", label: "Comisión neta" },
      { key: "incidents", label: "Incidencias" },
    ];
    const { topMetric } = this.state;

    arr.sort((a, b) => (b[topMetric] || 0) - (a[topMetric] || 0));
    const top5 = arr.slice(0, 5);

    // render tabs + table
    return (
      <div>
        <ul className="nav nav-tabs mb-2">
          {metrics.map((m) => (
            <li className="nav-item" key={m.key}>
              <button
                className={`nav-link ${topMetric === m.key ? "active" : ""}`}
                onClick={() => this.setState({ topMetric: m.key })}
                type="button"
              >
                {m.label}
              </button>
            </li>
          ))}
        </ul>
        <div className="table-responsive">
          <table className="table table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Cliente</th>
              <th>País</th>
              <th>GMV</th>
              <th>Comisión</th>
              <th>Tx</th>
              <th>Ocupación</th>
              <th>Disponibilidad</th>
              <th>Incidencias</th>
            </tr>
          </thead>
          <tbody>
            {top5.map((s, idx) => (
              <tr key={s.site}>
                <td>{idx + 1}</td>
                <td>{s.customer}</td>
                <td>{s.country}</td>
                <td>{fmtCurrency(s.gmv)}</td>
                <td>{fmtCurrency(s.commission)}</td>
                <td>{s.transactions}</td>
                <td>{s.avgOcc !== null ? pct(s.avgOcc) : "—"}</td>
                <td>{s.avgAvail !== null ? pct(s.avgAvail) : "—"}</td>
                <td>{s.incidents}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    );
  }

  // % máquinas reportando hoy
  computeReportingQuality() {
    const today = new Date();
    const twelveHoursAgo = new Date(today.getTime() - 12 * 60 * 60 * 1000);
    let arr = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos
      : [];
    // Only installed machines (same logic as donuts)
    arr = arr.filter(r =>
      (!r.installed_at || new Date(r.installed_at) <= today) &&
      (!r.removed_at || new Date(r.removed_at) > today)
    );
    const uniqueMachines = {};
    arr.forEach(r => {
      const machineId = r.MachineSerial || r.MachineId || r.Serial || r._id || "unknown";
      if (!uniqueMachines[machineId]) uniqueMachines[machineId] = r;
    });
    const total = Object.keys(uniqueMachines).length;
    const reporting = Object.values(uniqueMachines).filter(r => {
      if (!r.last_seen_at) return false;
      const lastSeen = new Date(r.last_seen_at);
      return lastSeen >= twelveHoursAgo;
    }).length;
    return { reporting, total, pct: total > 0 ? reporting / total : 0 };
  }

  renderReportingQuality() {
    const { reporting, total, pct } = this.computeReportingQuality();
    let color = pct >= 0.85 ? "#059669" : pct >= 0.7 ? "#F59E0B" : "#EF4444";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
        <i className="fas fa-broadcast-tower" style={{ color, fontSize: 16 }}></i>
        <span>
          % máquinas reportando hoy: <span style={{ color }}>{(pct * 100).toFixed(0)}%</span>
          <span style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>({reporting}/{total})</span>
        </span>
      </div>
    );
  }

  render() {
    const { error, role } = this.props;
    const {
      loading,
      startDate,
      endDate,
      compareStartDate,
      compareEndDate,
      country,
      businessModel,
      activeOnly,
      siteFilter,
      showAdvancedFilters,
      periodType,
      tempPeriodType,
      tempStartDate,
      tempEndDate,
    } = this.state;

    // derive list of customer names only; ignore coordinates entirely
    const customerOptions = (() => {
      let arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos.slice()
        : [];
      if (businessModel) {
        const map = {
          revenue_share: 'REVENUE SHARE',
          alquiler: 'RENTAL',
          venta: 'SALE',
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (country && country.trim()) {
        const q = country.trim().toLowerCase();
        arr = arr.filter(
          (r) =>
            (r.Country || r.CustomerCountry || r.SiteCountry || "")
              .toString()
              .toLowerCase()
              .includes(q),
        );
      }
      const names = arr
        .map((r) => r.CustomerName || "")
        .filter((v) => v && v.toString().trim())
        .map((v) => v.toString());
      return [...new Set(names)].sort();
    })();
    // determine whether there's enough input to fetch/cargar
    const canLoad = (() => {
      if (!tempStartDate) return false;
      switch (tempPeriodType) {
        case "range":
          return !!tempEndDate;
        case "day":
        case "month":
        case "year":
          return true;
        default:
          return false;
      }
    })();

    // compute default period values
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultStart = localIso(monthStart);
    const defaultEnd = localIso(today);

    // compute text to show in header
    const displayCurrentStart = startDate || defaultStart;
    const displayCurrentEnd = endDate || defaultEnd;

    // show comparison only if the user supplied both dates; otherwise leave
    // blank so we can render "(no comparison)" and avoid confusing LY data
    let displayCompareStart = compareStartDate;
    let displayCompareEnd = compareEndDate;
    // note: we no longer auto-populate the compare dates with last year
    // when the inputs are empty.  explicitly entering a range is required.

    const formatTitle = (start, end, type) => {
      if (!start) return "";
      switch (type) {
        case "range":
          return `${start} → ${end}`;
        case "day":
          return start;
        case "month":
          return start.slice(0, 7); // YYYY-MM
        case "year":
          return start.slice(0, 4);
        default:
          return `${start} → ${end}`;
      }
    };

    let periodTitle = formatTitle(
      displayCurrentStart,
      displayCurrentEnd,
      periodType,
    );
    if (!periodTitle) periodTitle = "(sin periodo)"; // no dates entered yet
    const hasCompareData = !!this.state.lyPersistedHasData;
    let compareTitleRaw = formatTitle(
      displayCompareStart,
      displayCompareEnd,
      periodType,
    );
    const compareTitle = compareTitleRaw
      ? hasCompareData
        ? compareTitleRaw
        : `${compareTitleRaw} (no data)`
      : "(no comparison)";

    // helper to know if comparison is exactly last year same period
    let isLyComparison = false;
    if (
      displayCurrentStart &&
      displayCurrentEnd &&
      displayCompareStart &&
      displayCompareEnd
    ) {
      const cs = new Date(displayCurrentStart);
      const ce = new Date(displayCurrentEnd);
      const lycs = new Date(cs);
      lycs.setFullYear(lycs.getFullYear() - 1);
      const lyce = new Date(ce);
      lyce.setFullYear(lyce.getFullYear() - 1);
      isLyComparison =
        displayCompareStart === localIso(lycs) &&
        displayCompareEnd === localIso(lyce);
    }
    // if there is no compare range at all, we will never treat it as LY
    // comparison (isLyComparison stays false)

    // prepare installed snapshot aggregations used by the three donut cards
    // `aggregateOperational()` already applies the active-only filter when
    // `this.state.activeOnly` is true, so the rows we work with here reflect
    // the current toggle state.
    let _opRows_forInstalled = this.aggregateOperational().raw || [];
    // ensure installed-summary rows are bound to current period as well
    const sd = this.state.startDate;
    const ed = this.state.endDate;
    if (sd || ed) {
      _opRows_forInstalled = _opRows_forInstalled.filter((r) => {
        const day = r.Day
          ? r.Day.slice
            ? r.Day.slice(0, 10)
            : String(r.Day).slice(0, 10)
          : null;
        if (!day) return false;
        if (sd && day < sd) return false;
        if (ed && day > ed) return false;
        return true;
      });
    }

    // we also need the list of *all* countries available prior to applying the
    // country filter (otherwise selecting one country leaves the select with
    // a single option). build a parallel set of rows that omit the country
    // filter but respect the other toggles.
    const _allCountryRows_forInstalled = (() => {
      const { activeOnly, businessModel } = this.state;
      let arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos
        : [];
      if (businessModel) {
        const map = {
          revenue_share: 'REVENUE SHARE',
          alquiler: 'RENTAL',
          venta: 'SALE',
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (activeOnly) {
        arr = arr.filter(
          (r) =>
            (Number(r.GMV_gross || r.SalesTotal || 0) > 0) ||
            (Number(r.TotalTickets || 0) > 0) ||
            !!r.isReporting,
        );
      }
      return arr;
    })();

    const currentAsOf = this.state.endDate ? new Date(this.state.endDate) : undefined;
    const _byCountry_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "Country",
      currentAsOf,
    );
    const _byCategory_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "Category",
      currentAsOf,
    );
    const _byBusiness_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "BusinessModel",
      currentAsOf,
    );

    // aggregate the ‘all countries’ rows as well for dropdown options
    const _allCountries_forInstalled = this.computeInstalledBy(
      _allCountryRows_forInstalled,
      "Country",
      currentAsOf,
    );

    // also prepare comparison aggregates if LY data is present
    // start with LY operational rows, but trim to the comparison end
    const _compareOpRows_forInstalled = Array.isArray(this.state.lyOperationalArqueos)
      ? this.state.lyOperationalArqueos.filter((r) => {
          if (this.state.compareEndDate && r.Day) {
            const day = new Date(r.Day.slice ? r.Day.slice(0, 10) : r.Day);
            if (day > new Date(this.state.compareEndDate)) {
              return false;
            }
          }
          return true;
        })
      : [];
    // apply same activeOnly filter as current period when requested
    let _filteredCompareRows =
      this.state.activeOnly && Array.isArray(_compareOpRows_forInstalled)
        ? _compareOpRows_forInstalled.filter(
            (r) =>
              (Number(r.GMV_gross || r.SalesTotal || 0) > 0) ||
              (Number(r.TotalTickets || 0) > 0) ||
              !!r.isReporting,
          )
        : _compareOpRows_forInstalled;
    // also apply business model filter client-side like in aggregate methods
    if (businessModel) {
      const map = {
        revenue_share: 'REVENUE SHARE',
        alquiler: 'RENTAL',
        venta: 'SALE',
      };
      const bm = map[businessModel] || businessModel;
      _filteredCompareRows = _filteredCompareRows.filter(r => r.BusinessModel === bm);
    }
    // also apply country filter to comparison rows
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      _filteredCompareRows = _filteredCompareRows.filter(
        r =>
          (r.Country || r.CustomerCountry || r.SiteCountry || "")
            .toString()
            .toLowerCase()
            .includes(q),
      );
    }
    const compareAsOf = this.state.compareEndDate ? new Date(this.state.compareEndDate) : undefined;
    const _byCountry_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "Country",
      compareAsOf,
    );
    const _byCategory_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "Category",
      compareAsOf,
    );
    const _byBusiness_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "BusinessModel",
      compareAsOf,
    );

    // also build the full list of comparison countries (ignoring the current
    // country filter) for the select box
    const _allCompareRows_forInstalled = (() => {
      let arr = _compareOpRows_forInstalled.slice();
      if (this.state.activeOnly) {
        arr = arr.filter(
          (r) =>
            (Number(r.GMV_gross || r.SalesTotal || 0) > 0) ||
            (Number(r.TotalTickets || 0) > 0) ||
            !!r.isReporting,
        );
      }
      if (businessModel) {
        const map = {
          revenue_share: 'REVENUE SHARE',
          alquiler: 'RENTAL',
          venta: 'SALE',
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter(r => r.BusinessModel === bm);
      }
      return arr;
    })();
    const _allCountries_forInstalledCompare = this.computeInstalledBy(
      _allCompareRows_forInstalled,
      "Country",
      compareAsOf,
    );

    const _totalInstalledAll = this.computeInstalledBy(_opRows_forInstalled, "Country", currentAsOf).total;

    if (role !== "ADMIN") return <ErrorMessage message="Not authorized" />;

    return (
      <div className="dashboard-view">
        {/* Header (reuse FaultsView styles) */}
        <div
          className="dashboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-chart-line"></i> Dashboard Operativo
            </h1>
            <div className="small text-muted mt-1">
              Período: {periodTitle} (vs {compareTitle})
            </div>
          </div>

          <div className="dashboard-actions">
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: 8 }}
            >
              <input
                type="text"
                list="customer-options"
                className="dashboard-filter-input"
                placeholder="Filtrar por cliente"
                value={siteFilter}
                onChange={(e) => this.setState({ siteFilter: e.target.value })}
                style={{ width: 260 }}
              />
              <datalist id="customer-options">
                {customerOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {/* country filter dropdown - options driven by the installed donuts */}
              <select
                className="dashboard-filter-input"
                value={country}
                onChange={(e) => this.setState({ country: e.target.value })}
              >
                <option value="">País (todos)</option>
                {(() => {
                  // derive options from the lists that ignore the active country
                  // filter so that changing the selection doesn't collapse the
                  // menu to a single entry.
                  const base = Array.isArray(_allCountries_forInstalled.items)
                    ? _allCountries_forInstalled.items.map((i) => i.key)
                    : [];
                  let comp = [];
                  if (hasCompareData && Array.isArray(_allCountries_forInstalledCompare.items)) {
                    comp = _allCountries_forInstalledCompare.items.map((i) => i.key);
                  }
                  const all = [...new Set([...base, ...comp])];
                  return all.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ));
                })()}
              </select>
              <select
                className="dashboard-filter-input"
                value={businessModel}
                onChange={(e) =>
                  this.setState({ businessModel: e.target.value })
                }
              >
                <option value="">Modelo negocio (todos)</option>
                <option value="revenue_share">Revenue-share</option>
                <option value="alquiler">Alquiler (Rental)</option>
                <option value="venta">Venta (Sale)</option>
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label className="small text-muted" style={{ margin: 0 }}>
                  Solo ubicaciones activas
                </label>
                <label className="dashboard-switch" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={activeOnly}
                    onChange={(e) =>
                      this.setState({ activeOnly: e.target.checked })
                    }
                  />
                  <span className="slider round" />
                </label>
              </div>
            </div>
            {/* toggle button floated right with indicator */}
            <button
              className="btn btn-vw btn-sm p-1 toggle-filters"
              onClick={() =>
                this.setState({ showAdvancedFilters: !showAdvancedFilters })
              }
              style={{ display: "flex", alignItems: "center" }}
            >
              {showAdvancedFilters ? (
                <>
                  <i className="fas fa-chevron-up mr-1"></i>Ocultar filtros
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down mr-1"></i>Mostrar filtros
                </>
              )}
            </button>
          </div>
        </div>
        {showAdvancedFilters && (
          <div
            className="dashboard-header mb-3"
            style={{ padding: "12px 20px", justifyContent: "center" }}
          >
            <div
              className="d-flex align-items-center flex-wrap justify-content-center"
              style={{ gap: 8 }}
            >
              {/* period type selector */}
              <select
                className="filter-input"
                value={this.state.tempPeriodType}
                onChange={(e) =>
                  this.setState({
                    tempPeriodType: e.target.value,
                    tempStartDate: "",
                    tempEndDate: "",
                    tempCompareStartDate: "",
                    tempCompareEndDate: "",
                  })
                }
                style={{ width: 140 }}
              >
                <option value="range">Rango</option>
                <option value="day">Día</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>

              {/* origin inputs, change based on period type */}
              {this.state.tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="filter-input"
                    title="Origen inicio"
                    value={this.state.tempStartDate}
                    onChange={(e) =>
                      this.setState({ tempStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="filter-input"
                    title="Origen fin"
                    value={this.state.tempEndDate}
                    onChange={(e) =>
                      this.setState({ tempEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input
                  type="date"
                  className="filter-input"
                  title="Origen (día)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "month" && (
                <input
                  type="month"
                  className="filter-input"
                  title="Origen (mes)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="filter-input"
                  title="Origen (año)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}

              <span
                className="small text-muted"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                comp. <em>(leave blank to disable)</em>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{
                    padding: "0 8px",
                    fontSize: "0.8rem",
                    height: "36px",
                    lineHeight: "36px",
                  }}
                  title="Autofill comparación mismo período año anterior"
                  onClick={this.fillLastYear}
                  disabled={!this.state.tempStartDate}
                >
                  Año ant.
                </button>
              </span>

              {/* compare inputs */}
              {this.state.tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="filter-input"
                    title="Comparación inicio (dejar vacío para desactivar)"
                    value={this.state.tempCompareStartDate}
                    onChange={(e) =>
                      this.setState({ tempCompareStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="filter-input"
                    title="Comparación fin (dejar vacío para desactivar)"
                    value={this.state.tempCompareEndDate}
                    onChange={(e) =>
                      this.setState({ tempCompareEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input
                  type="date"
                  className="filter-input"
                  title="Comparación (día)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "month" && (
                <input
                  type="month"
                  className="filter-input"
                  title="Comparación (mes)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="filter-input"
                  title="Comparación (año)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}



              <button
                className="btn btn-vw ml-2"
                onClick={async () => {
                  // translate temp inputs according to period type
                  const {
                    tempPeriodType,
                    tempStartDate,
                    tempEndDate,
                    tempCompareStartDate,
                    tempCompareEndDate,
                  } = this.state;

                  const normalize = (type, val1, val2) => {
                    let s = "";
                    let e = "";
                    if (!val1) return { s: "", e: "" };
                    switch (type) {
                      case "range":
                        s = val1;
                        e = val2;
                        break;
                      case "day":
                        s = val1;
                        e = val1;
                        break;
                      case "month": {
                        // val1 like YYYY-MM
                        const [y, m] = val1.split("-");
                        if (y && m) {
                          s = `${y}-${m}-01`;
                          const last = localIso(new Date(y, m, 0));
                          e = last;
                        }
                        break;
                      }
                      case "year": {
                        const y = val1;
                        if (y) {
                          s = `${y}-01-01`;
                          e = `${y}-12-31`;
                        }
                        break;
                      }
                    }
                    return { s, e };
                  };

                  const { s: startDate, e: endDate } = normalize(
                    tempPeriodType,
                    tempStartDate,
                    tempEndDate,
                  );
                  const { s: compareStartDate, e: compareEndDate } = normalize(
                    tempPeriodType,
                    tempCompareStartDate,
                    tempCompareEndDate,
                  );

                  await this.setState({
                    startDate,
                    endDate,
                    compareStartDate,
                    compareEndDate,
                    periodType: tempPeriodType,
                  });
                  this.load();
                }}
                disabled={loading || !canLoad}
              >
                {loading ? "Loading..." : "Cargar"}
              </button>
            </div>
          </div>
        )}

        {/* KPI cards row (styled via dashboard-kpi-row for consistent admin look) */}
        {error && <ErrorMessage message={error.message} />}

        {loading ? (
          <LoadingProgress />
        ) : (
          <div className="dashboard-kpi-wrapper pb-3">
            <div className="dashboard-kpi-row mb-3">
              {(() => {
                const current = this.aggregateSales();
                const op = this.aggregateOperational();
                let lySales = Array.isArray(this.state.lySalesArqueos)
                  ? this.state.lySalesArqueos.slice()
                  : [];
                let lyOpRows = Array.isArray(this.state.lyOperationalArqueos)
                  ? // restrict LY operational rows to comparison end date as well
                    this.state.lyOperationalArqueos.filter((r) => {
                      if (this.state.compareEndDate && r.Day) {
                        const day = new Date(r.Day.slice ? r.Day.slice(0, 10) : r.Day);
                        return day <= new Date(this.state.compareEndDate);
                      }
                      return true;
                    })
                  : [];
                // apply same bm mapping as above
                if (businessModel) {
                  const map = {
                    revenue_share: 'REVENUE SHARE',
                    alquiler: 'RENTAL',
                    venta: 'SALE',
                  };
                  const bm = map[businessModel] || businessModel;
                  lySales = lySales.filter(r => r.BusinessModel === bm);
                  lyOpRows = lyOpRows.filter(r => r.BusinessModel === bm);
                }
                // apply country filter to LY arrays too
                if (country && country.trim()) {
                  const q = country.trim().toLowerCase();
                  lySales = lySales.filter(
                    r =>
                      (r.CustomerCountry || r.Country || "")
                        .toString()
                        .toLowerCase()
                        .includes(q),
                  );
                  lyOpRows = lyOpRows.filter(
                    r =>
                      (r.Country || r.CustomerCountry || "")
                        .toString()
                        .toLowerCase()
                        .includes(q),
                  );
                }
                const lyAgg = this.aggregateFromRows(lySales);
                const lyOpAgg = this.aggregateOperationalFromRows(lyOpRows);

                const renderDelta = (curNum, lyNum, opts = {}) => {
                  // opts: {type: 'currency'|'percent'|'points'|'count'}
                  // if there was no persisted comparison data or the LY value is
                  // missing/zero we can't compute a meaningful delta – always show
                  // n/a instead of pretending there was an 100%+ drop.
                  if (
                    !hasCompareData ||
                    lyNum === null ||
                    lyNum === undefined ||
                    lyNum === 0
                  ) {
                    return (
                      <div className="small text-muted">
                        {isLyComparison ? "vs LY: n/a" : "vs comp: n/a"}
                      </div>
                    );
                  }

                  const deltaAbs = (curNum || 0) - (lyNum || 0);
                  const deltaPct = lyNum !== 0 ? deltaAbs / (lyNum || 1) : null;
                  const positive = deltaAbs > 0;
                  const colorClass = positive
                    ? "text-success"
                    : deltaAbs < 0
                      ? "text-danger"
                      : "text-muted";
                  const arrow = deltaAbs > 0 ? "▲" : deltaAbs < 0 ? "▼" : "";

                  const prefix = (
                    <span className="text-muted">
                      {isLyComparison ? "vs LY:" : "vs comp:"}&nbsp;
                    </span>
                  );

                  if (opts.type === "currency") {
                    const absVal = Math.abs(deltaAbs);
                    return (
                      <div className="small">
                        {prefix}
                        <span className={colorClass}>
                          {arrow}{" "}
                          {deltaPct !== null
                            ? `${(deltaPct * 100).toFixed(1)}%`
                            : "—"}{" "}
                          {` (${deltaAbs >= 0 ? "+" : "-"}€${fmtCurrency(absVal)})`}
                        </span>
                      </div>
                    );
                  }

                  if (opts.type === "points") {
                    const points = (deltaAbs * 100).toFixed(2);
                    return (
                      <div className="small">
                        {prefix}
                        <span className={colorClass}>
                          {arrow} {points} pp{" "}
                          {deltaPct !== null
                            ? `(${(deltaPct * 100).toFixed(1)}%)`
                            : ""}
                        </span>
                      </div>
                    );
                  }

                  // default: count/percent
                  return (
                    <div className="small">
                      {prefix}
                      <span className={colorClass}>
                        {arrow}{" "}
                        {deltaPct !== null
                          ? `${(deltaPct * 100).toFixed(1)}%`
                          : "—"}{" "}
                        {` (${deltaAbs >= 0 ? "+" : ""}${deltaAbs})`}
                      </span>
                    </div>
                  );
                };

                const makeCard = (
                  cls,
                  icon,
                  label,
                  curValueFormatted,
                  curRaw,
                  lyRaw,
                  deltaOpts,
                ) => (
                  <div
                    className={`dashboard-kpi-card ${cls}`}
                    key={label}
                    style={{
                      minHeight: 96,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      padding: "10px 12px",
                    }}
                  >
                    {/* top: icon + title */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <i
                        className={`fas fa-${icon}`}
                        style={{ fontSize: 12, color: "#6b7280" }}
                      ></i>
                      <div
                        className="kpi-label"
                        style={{ margin: 0, fontSize: 12 }}
                      >
                        {label}
                      </div>
                    </div>

                    {/* middle: large value */}
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="kpi-value"
                        style={{ fontSize: 20, fontWeight: 700 }}
                      >
                        {curValueFormatted}
                      </div>
                    </div>

                    {/* bottom: LY variation */}
                    <div style={{ marginTop: 8 }}>
                      {renderDelta(curRaw, lyRaw, deltaOpts)}
                    </div>
                  </div>
                );

                // Build KPI cards with LY comparisons (positive -> green, negative -> red)
                const currentTakeRate =
                  current.gmv > 0 ? current.commission / current.gmv : null;
                const lyTakeRate =
                  lyAgg.gmv > 0 ? lyAgg.commission / lyAgg.gmv : null;

                return [
                  makeCard(
                    "total",
                    "coins",
                    "GMV",
                    `${fmtCurrency(current.gmv)} €`,
                    current.gmv,
                    lyAgg.gmv,
                    { type: "currency" },
                  ),
                  makeCard(
                    "green-block",
                    "hand-holding-usd",
                    "Comisión neta",
                    `${fmtCurrency(current.commission)} €`,
                    current.commission,
                    lyAgg.commission,
                    { type: "currency" },
                  ),
                  makeCard(
                    "pinpad-active",
                    "percent",
                    "Take rate",
                    currentTakeRate !== null ? pct(currentTakeRate) : "—",
                    currentTakeRate,
                    lyTakeRate,
                    { type: "points" },
                  ),
                  makeCard(
                    "pinpad-block",
                    "receipt",
                    "Transacciones",
                    current.transactions,
                    current.transactions,
                    lyAgg.transactions,
                    { type: "count" },
                  ),
                  makeCard(
                    "red-block",
                    "ticket-alt",
                    "Ticket medio",
                    current.ticketAvg
                      ? `${fmtCurrency(current.ticketAvg)} €`
                      : "—",
                    current.ticketAvg,
                    lyAgg.ticketAvg,
                    { type: "currency" },
                  ),
                  makeCard(
                    "broken",
                    "chart-area",
                    "Ocupación ajustada",
                    op.avgOccFinal !== null ? pct(op.avgOccFinal) : "—",
                    op.avgOccFinal,
                    lyOpAgg.avgOccFinal,
                    { type: "points" },
                  ),
                  makeCard(
                    "door-block",
                    "tools",
                    "Disponibilidad",
                    op.avgAvailFinal !== null ? pct(op.avgAvailFinal) : "—",
                    op.avgAvailFinal,
                    lyOpAgg.avgAvailFinal,
                    { type: "points" },
                  ),
                  makeCard(
                    "pinpad-block",
                    "exclamation-circle",
                    "Incidencias",
                    op.incidences,
                    op.incidences,
                    lyOpAgg.incidences,
                    { type: "count" },
                  ),
                ];
              })()}
            </div>

            {/* Installed donuts — three separate cards (each: header + donut/list columns) */}
            <div className="dashboard-kpi-row mb-3">
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData ? _byCountry_forInstalledCompare : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por país",
                      _byCountry_forInstalled,
                      "country",
                      "globe",
                      _totalInstalledAll,
                      comp,
                    );
                  })()}
                </div>
              </div>
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData ? _byCategory_forInstalledCompare : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por vertical",
                      _byCategory_forInstalled,
                      "category",
                      "layer-group",
                      _totalInstalledAll,
                      comp,
                    );
                  })()}
                </div>
              </div>
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData ? _byBusiness_forInstalledCompare : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por modelo negocio",
                      _byBusiness_forInstalled,
                      "businessModel",
                      "briefcase",
                      _totalInstalledAll,
                      comp,
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Locker KPIs row (GMV per locker, Commission per locker, Trend) */}
            {this.renderLockerKPIs()}

            {/* top-five clients card wrapped in same grid/spacing as KPI row */}
            <div className="dashboard-kpi-row mb-3">
              <div
                className="dashboard-card-section mb-0"
                style={{ width: "100%" }}
              >
                <div className="card-header-custom">
                  <i className="fas fa-list-ol mr-2"></i>Top 5 clientes
                </div>
                <div className="card-body-custom">
                  {this.renderTopLocations()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  generatedSalesArqueos: state.arqueos.generatedSalesArqueos,
  generatedOperationalArqueos: state.arqueos.generatedOperationalArqueos,
  error: state.arqueos.error,
  role: state.auth.role,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPersistedSalesArqueos,
      fetchPersistedOperationalArqueos,
      fetchGeneratedSalesArqueos:
        require("../actions/arqueos").fetchGeneratedSalesArqueos,
      fetchGeneratedOperationalArqueos:
        require("../actions/arqueos").fetchGeneratedOperationalArqueos,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperationalDashboardView);
